import React, {useRef} from 'react'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import ContentReader from '@components/ContentReader'
import PropTypes from 'prop-types'

import * as classes from './index.module.css'
import AnimatedImage from '@Ui/AnimatedImage'
import { Link } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import LinkIcon from '@Src/svgs/Arrow'
import AnimatedText from '../Ui/AnimatedText'
import Quote from '../Quote'
import Banner from '../Ui/Banner'


const getHeadingTag = (heading) => {
	switch(heading) {
		case 'Heading 1': return 'h1'
		case 'Heading 2': return 'h2'
		case 'Heading 3': return 'h3'
		case 'Heading 4': return 'h4'
		case 'Heading 5': return 'h5'
		case 'Heading 6': return 'h6'
		case 'Paragraph': return 'p'
		case 'Span': return 'span'
		case 'Div': return 'div'
		default: return null;
	}
}

// ✅ FIXED: Heading now supports refs
const Heading = React.forwardRef(({heading, children, customClasses, defaults, ...props}, ref) => {
	const CustomTag = getHeadingTag(heading) ? getHeadingTag(heading) : defaults;
	return (
		<CustomTag ref={ref} className={customClasses} {...props}>
			{children}
		</CustomTag>
	)
});

Heading.propTypes = {
	heading: PropTypes.string,
	customClasses: PropTypes.string,
	defaults: PropTypes.string,
	children: PropTypes.array,
}

const TARGET_DOMAIN = 'https://wearemtm.com';

const SmartLink = ({ to = '', children, ...props }) => {
  // Check if it starts with internal slash, matches target domain, or current origin in browser
  const isRelative = /^\/(?!\/)/.test(to);
  const isTargetDomain = to.startsWith(TARGET_DOMAIN);
  const isCurrentOrigin =
    typeof window !== 'undefined' && to.startsWith(window.location.origin);

  const isInternal = isRelative || isTargetDomain || isCurrentOrigin;

  if (isInternal) {
    // Strip absolute domains to leave a relative path Gatsby's Link expects
    let internalPath = to;

    if (isTargetDomain) {
      internalPath = to.replace(TARGET_DOMAIN, '');
    } else if (isCurrentOrigin) {
      internalPath = to.replace(window.location.origin, '');
    }

    // Ensure empty paths fallback to root
    internalPath = internalPath.startsWith('/') ? internalPath : `/${internalPath}`;

    return (
      <Link to={internalPath} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <a href={to} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
};

const RichText = ({
	content, 
	ele, 
	useHeadings, 
	slug, 
	relatedArticles, 
	staticImages = false ,  
	splitType="words", 
	direction = 'right',
	delay = 0,
	useAnimate = false, 
	endQuote = false,
	...props
}) => {

	const animatedTextRef = useRef();

	const getBackLink = (link, children) => {
		if (!relatedArticles) return <strong className={classes.bold}>{children}</strong>; 
		const relatedArticle = relatedArticles?.find(article => article.tags.includes(children[0][0]));
		return (
			relatedArticle ?
				<a href={`/${relatedArticle.parentCategory.slug}/${relatedArticle.slug}`} className={classes.link} title={relatedArticle.title}>
					{children}
				</a>
			:
				<strong className={classes.bold}>{children}</strong>
		)
	}

	const options = {
		renderMark: {
			[MARKS.BOLD]: (text) => <strong className={classes.bold}>{text}</strong>,
			[MARKS.ITALIC]: (text) => <em className={classes.italics}>{text}</em>,
			[MARKS.UNDERLINE]: text => <u className={classes.underLines}>{text}</u>,
			[MARKS.SUBSCRIPT]: text => <sub className={classes.sub}>{text}</sub>,
			[MARKS.SUPERSCRIPT]: text => <sup className={classes.sup}>{text}</sup>,
			[MARKS.CODE]: text => <code className={classes.code}>{text}</code>,
		},
		renderNode: {

			// --------------------------
			// PARAGRAPH
			// --------------------------
			[BLOCKS.PARAGRAPH]: (node, children) => {
				// Check if the paragraph is empty or contains only whitespace
				const isEmpty = !children || children.length === 0 || children.every(c => String(c).trim() === '');

				if (isEmpty) {
					// Render only a <br /> if empty
					return <br />;
				}

				// Otherwise, render normally inside Heading
				return (
					<Heading
					ref={animatedTextRef}
					defaults="p"
					heading={useHeadings}
					customClasses={`${classes.paragraph} ${useAnimate ? classes.animated : ''}`} 
					{...props}
					>
					{useAnimate ? (
						<AnimatedText text={children} splitType={splitType} direction={direction} delay={delay}/>
					) : (
						<>{children}</>
					)}
					</Heading>
				);
			},

			// --------------------------
			// HEADINGS
			// --------------------------
			[BLOCKS.HEADING_1]: (node, children) => (
				<Heading ref={animatedTextRef} defaults="h1" heading={useHeadings} customClasses={`${classes.heading_1} ${useAnimate ? classes.animated : ''}`}>
					{useAnimate ? <AnimatedText text={children} splitType={splitType} direction={direction} delay={delay}/> : children}
				</Heading>
			),
			[BLOCKS.HEADING_2]: (node, children) => (
				<Heading ref={animatedTextRef} defaults="h2" heading={useHeadings} customClasses={`${classes.heading_2} ${useAnimate ? classes.animated : ''}`}>
					{useAnimate ? <AnimatedText text={children} splitType={splitType} direction={direction} delay={delay}/> : children}
				</Heading>
			),
			[BLOCKS.HEADING_3]: (node, children) => (
				<Heading ref={animatedTextRef} defaults="h3" heading={useHeadings} customClasses={`${classes.heading_3} ${useAnimate ? classes.animated : ''}`}>
					{useAnimate ? <AnimatedText text={children} splitType={splitType} direction={direction} delay={delay}/> : children}
				</Heading>
			),
			[BLOCKS.HEADING_4]: (node, children) => (
				<Heading ref={animatedTextRef} defaults="h4" heading={useHeadings} customClasses={`${classes.heading_4} ${useAnimate ? classes.animated : ''}`}>
					{useAnimate ? <AnimatedText text={children} splitType={splitType} direction={direction} delay={delay}/> : children}
				</Heading>
			),
			[BLOCKS.HEADING_5]: (node, children) => (
				<Heading ref={animatedTextRef} defaults="h5" heading={useHeadings} customClasses={`${classes.heading_5} ${useAnimate ? classes.animated : ''}`}>
					{useAnimate ? <AnimatedText text={children} splitType={splitType} direction={direction} delay={delay}/> : children}
				</Heading>
			),
			[BLOCKS.HEADING_6]: (node, children) => (
				<Heading ref={animatedTextRef} defaults="h6" heading={useHeadings} customClasses={`${classes.heading_6} ${useAnimate ? classes.animated : ''}`}>
					{useAnimate ? <AnimatedText text={children} splitType={splitType} direction={direction} delay={delay}/> : children}
				</Heading>
			),

			// --------------------------
			// LISTS, TABLES, QUOTES etc.
			// --------------------------
			[BLOCKS.OL_LIST]: (node, children) => <ol className={classes.olList}>{children}</ol>,
			[BLOCKS.UL_LIST]: (node, children) => <ul className={classes.ulList}>{children}</ul>,
			[BLOCKS.LIST_ITEM]: (node, children) => <li className={classes.li}>{children}</li>,

			[BLOCKS.TABLE]: (node, children) => (
				<div className={classes.tableWrapper}>
					<table className={classes.table}><tbody>{children}</tbody></table>
				</div>
			),
			[BLOCKS.TABLE_ROW]: (node, children) => <tr className={classes.tableRow}>{children}</tr>,
			[BLOCKS.TABLE_HEADER_CELL]: (node, children) => <th className={classes.tableHead}>{children}</th>,
			[BLOCKS.TABLE_CELL]: (node, children) => <td className={classes.tableCell}>{children}</td>,
			[BLOCKS.HR]: () => <hr className={classes.hr}/>,

			[BLOCKS.QUOTE]: (node, children) => <Quote text={children}/>,

			// --------------------------
			// IMAGES
			// --------------------------
			[BLOCKS.EMBEDDED_ASSET]: (node) => {
				const { title, gatsbyImageData } = node.data.target;
				return staticImages ? (
					<GatsbyImage image={gatsbyImageData} alt={title} />
				) : (
					<div className={classes.imageContainer} data-image-wrapper>
						<AnimatedImage image={node.data.target} className={classes.image}/>
					</div>
				)
			},

			// --------------------------
			// ENTRIES
			// --------------------------
			[BLOCKS.EMBEDDED_ENTRY]: node => <ContentReader data={node.data.target} />,
			['embedded-entry-inline']: node => <ContentReader data={node.data.target} />,

			// --------------------------
			// LINKS
			// --------------------------
			[INLINES.HYPERLINK]: (node, children) => (
				node.data.uri.includes('?keyword=')
					? getBackLink(node.data.uri, children)
					: <SmartLink to={node.data.uri} className={classes.link}>{children}</SmartLink>
			),

			[INLINES.ASSET_HYPERLINK]: (node, children) => {
				const url = node?.data?.target?.file?.url || '';
				const domain = 'https://wearemtm.com';

				if (url.includes(domain)) {
					// Strip the domain so Gatsby Link handles it as an internal route
					const internalPath = url.replace(domain, '') || '/';

					return (
					<Link to={internalPath} className={classes.link}>
						{children}
					</Link>
					);
				}

				return (
					<a href={url} className={classes.link}>
					{children}
					</a>
				);
			},

			[INLINES.ENTRY_HYPERLINK]: (node, children) => {
				const block = node.data.target
				return <Link to={block.url}>{children}</Link>
			},
		},

		renderText: text =>
			React.Children.toArray(
				text.split('\n').map((t, i) =>
					i > 0 ? (
						<>
							{useHeadings ? <span className={classes.br}></span> : <br />}
							{`${t}${endQuote ? '”' : ''}`}
						</>
					) : `${t}${endQuote ? '”' : ''}`
				)
			)
	}

	return (
		<div ref={ele} {...props}>
			{renderRichText(content, options)}
		</div>
	)
}

export default RichText

RichText.propTypes = {
	content: PropTypes.object,
	useHeadings: PropTypes.string,
	ele: PropTypes.object,
};
